import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { ProvenancePanel } from '../provenance-panel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Tabs } from 'radix-ui';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { ErrorState, LoadingState } from '../primitives';
import type { CanvasTarget } from '../types';
import {
  agentErrorMessage,
  agentsApi,
  announceAgentsChanged,
  onAgentsChanged,
  RUNS_SHOWN,
  type Agent,
  type AgentRun,
  type AgentVersions,
} from './api';
import { RunsTab } from './runs';
import { emptyTurnContext } from '../types';
import { CLOSES_CANVAS } from './shared';
import { VersionsTab } from './versions';
import './agents.css';
import { CanvasFooter } from '../canvas-footer';
import { AgentRunButton } from './run-controls';
import { CanvasActivityTab } from '../canvas-activity-tab';
import { useCanvasCopy } from '../canvas-copy';
import { AgentFooter, liveAuthorWarning } from './footer';
import { AgentSections, NewAgentParts, type AgentPart } from './sections';
import { AgentShareChip } from './share';
import { isNewAgentCanvas } from './new-agent';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPresentationCopy from '@/i18n/ns/agent-presentation';
import editorCopy from '@/i18n/ns/workspace-editors';

// The agent canvas (agent redesign 2026-10-09): the agent as a readable
// document under Agent, its Runs and its Versions, with who can use it in the
// canvas bar and one footer that says where it stands. An agent never created
// (a draft) is made here instead: Agent and Test run, Test run and Create in
// the footer. The server decides every permission; this shows what `access`
// says.

type AgentTab = 'agent' | 'runs' | 'versions' | 'test' | 'activity';

// Older links name the tabs this canvas had: they open where that content lives now.
const TAB_ALIASES: Record<string, AgentTab> = {
  setup: 'agent',
  triggers: 'agent',
  access: 'agent',
  changes: 'versions',
};

function resolveTab(requested: string | undefined, tabs: AgentTab[]): AgentTab {
  const tab = (requested && TAB_ALIASES[requested]) || (requested as AgentTab | undefined);
  if (tab && tabs.includes(tab)) return tab;
  // A draft's runs are its test runs.
  return tab === 'runs' && tabs.includes('test') ? 'test' : 'agent';
}

/** "Version 2 · Company agent · Made by Anna Lind"; a draft is "New agent · Draft". */
function metaLine(
  agent: Agent,
  draft: boolean,
  companyName: string,
  copy: Readonly<Record<keyof typeof agentPresentationCopy.en, string> & { versionLabel: string }>,
) {
  if (draft) return copy.newAgentDraft;
  return [
    agent.publishedRevision
      ? formatCopy(copy.versionLabel, { version: agent.publishedRevision })
      : copy.notCreated,
    agent.scope === 'company' ? copy.companyAgent : copy.personalAgent,
    agent.owner
      ? formatCopy(copy.madeBy, { name: agent.owner.name ?? copy.formerMember })
      : formatCopy(copy.adminsManage, { company: companyName }),
  ].join(' · ');
}

const count = (value: number, most = Number.POSITIVE_INFINITY) =>
  value >= most ? `${most}+` : String(value);

export function AgentCanvas({ target }: { target: CanvasTarget & { id: string } }) {
  if (isNewAgentCanvas(target)) return <NewAgentCanvas />;
  // One agent's open editors and loaded runs never carry over to the next agent opened here.
  return <SavedAgentCanvas key={target.id} target={target} />;
}

function SavedAgentCanvas({ target }: { target: CanvasTarget & { id: string } }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const ws = useWorkspace();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const canvasCopy = useCanvasCopy();
  const editor = useNamespaceCopy(editorCopy);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [versions, setVersions] = useState<AgentVersions | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const inFlight = useRef(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((value) => value + 1), []);
  useEffect(() => onAgentsChanged(reload), [reload]);
  useEffect(() => {
    let active = true;
    setError('');
    Promise.all([
      agentsApi.get(target.id),
      agentsApi.runs(target.id),
      agentsApi.versions(target.id),
    ])
      .then(([loaded, loadedRuns, loadedVersions]) => {
        if (!active) return;
        setAgent(loaded);
        setRuns(loadedRuns);
        setVersions(loadedVersions);
      })
      .catch((failure) => {
        if (active) setError(agentErrorMessage(failure, locale));
      });
    return () => {
      active = false;
    };
  }, [target.id, tick, locale]);
  // An older Access link opens the Share sheet, including its editor controls.
  const opensAccess = target.tab === 'access' && Boolean(agent?.access.edit);
  useEffect(() => {
    if (opensAccess) setAccessOpen(true);
  }, [opensAccess, target.id]);
  const act = async (work: () => Promise<unknown>, done?: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice('');
    try {
      const outcome = await work();
      announceAgentsChanged();
      if (outcome === CLOSES_CANVAS) {
        if (done) ws.notify(done);
        ws.openCanvas(null);
        return;
      }
      if (done) setNotice(done);
      reload();
    } catch (failure) {
      setNotice(agentErrorMessage(failure, locale));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!agent) return <LoadingState label={uiText('Loading agent…')} />;
  const draft = agent.publishedRevision === null && agent.access.edit;
  const tabs: AgentTab[] = draft ? ['agent', 'test'] : ['agent', 'runs', 'versions'];
  if (target.activityMessageId) tabs.push('activity');
  const tab = resolveTab(target.tab, tabs);
  const selectTab = (next: string) => ws.openCanvas({ ...target, tab: next });
  const changeAccessOpen = (open: boolean) => {
    setAccessOpen(open);
    // The Access link has done its work: the canvas stays on the Agent tab.
    if (!open && target.tab === 'access')
      ws.openCanvas({ ...target, tab: 'agent' }, { replace: true });
  };
  const runNow = async () => {
    if (inFlight.current || !agent.access.run || !agent.publishedRevision) return;
    inFlight.current = true;
    const ownsNavigation = ws.beginNavigation();
    setBusy(true);
    setNotice('');
    try {
      const started = await agentsApi.run(agent.id);
      if (ownsNavigation()) ws.openThread(started.threadId);
    } catch (failure) {
      if (ownsNavigation()) setNotice(agentErrorMessage(failure, locale));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const testRuns = runs.filter((run) => run.triggerType === 'test');
  const warning = liveAuthorWarning(agent, agentCopy);
  const labels: Record<AgentTab, string> = {
    agent: editor.agentTab,
    runs: editor.runsTab,
    versions: editor.versionsTab,
    test: editor.testRunTab,
    activity: canvasCopy.activity,
  };
  const counts: Partial<Record<AgentTab, string>> = {
    runs: count(runs.length, RUNS_SHOWN),
    versions: count(versions?.versions.length ?? 0),
  };
  return (
    <Tabs.Root
      className="ws-agents-canvas"
      data-draft={draft ? 'true' : undefined}
      value={tab}
      onValueChange={selectTab}
    >
      <AgentShareChip
        agent={agent}
        busy={busy}
        act={act}
        accessOpen={accessOpen}
        onAccessOpen={changeAccessOpen}
      />
      <header className="ws-canvas-body ws-canvas-heading ws-agents-heading">
        <p className="ws-agents-meta">
          {metaLine(agent, draft, ws.companyName, {
            ...agentCopy,
            versionLabel: editor.versionMeta,
          })}
        </p>
        <h1>{agent.name}</h1>
        {warning && (
          <p className="ws-agents-note" role="note">
            <WorkspaceIcon name="eye" size={14} />
            <span>{warning}</span>
          </p>
        )}
        {!draft && (agent.access.run || agent.access.edit) && (
          <div className="ws-actions ws-agents-head-actions">
            <AgentRunButton agent={agent} busy={busy} onRun={runNow} primary />{' '}
            {agent.access.edit && (
              <button
                className="ws-button"
                onClick={() =>
                  ws.send(
                    formatCopy(editor.editAgentChatPrompt, { id: agent.id, name: agent.name }),
                    emptyTurnContext(),
                    true,
                  )
                }
              >
                {uiRich('{value1} Edit in chat', {
                  value1: <WorkspaceIcon name="edit" size={14} />,
                })}
              </button>
            )}
          </div>
        )}
        {notice && (
          <p className="ws-page-note" role="status">
            {notice}
          </p>
        )}
      </header>
      <Tabs.List
        className="ws-view-tabs ws-canvas-tabs ws-agents-tabs"
        aria-label={uiText('Agent')}
      >
        {tabs.map((key) => (
          <Tabs.Trigger key={key} value={key} className={tab === key ? 'active' : ''}>
            {labels[key]}
            {counts[key] !== undefined && (
              <span className="ws-agents-tab-count">{counts[key]}</span>
            )}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      <Tabs.Content
        value="agent"
        forceMount
        hidden={tab !== 'agent'}
        className="ws-canvas-body ws-agents-body"
      >
        {(agent.draft ?? agent.published) ? (
          <AgentSections
            agent={agent}
            busy={busy}
            act={act}
            draft={draft}
            focus={target.tab === 'triggers' ? ('schedule' as AgentPart) : null}
            onReviewDraft={() => selectTab('versions')}
          />
        ) : (
          <p className="ws-agents-note">
            <WorkspaceIcon name="eye" size={14} />
            <span>
              {uiText('This agent has not been created yet. It can run once its owner creates it.')}
            </span>
          </p>
        )}
      </Tabs.Content>
      <Tabs.Content value="runs" className="ws-canvas-body ws-agents-body">
        {tab === 'runs' && <RunsTab agent={agent} runs={runs} busy={busy} act={act} />}
      </Tabs.Content>
      <Tabs.Content value="test" className="ws-canvas-body ws-agents-body">
        {tab === 'test' && (
          <RunsTab
            agent={agent}
            runs={testRuns}
            busy={busy}
            act={act}
            empty={agentCopy.noTestRuns}
          />
        )}
      </Tabs.Content>
      <Tabs.Content value="versions" className="ws-canvas-body ws-agents-body">
        {tab === 'versions' && (
          <VersionsTab agent={agent} versions={versions} busy={busy} act={act} />
        )}
      </Tabs.Content>
      <Tabs.Content
        value="activity"
        forceMount
        hidden={tab !== 'activity'}
        className="ws-canvas-body ws-agents-body"
      >
        {tab === 'activity' && <CanvasActivityTab target={target} />}
      </Tabs.Content>
      <div className="ws-canvas-body">
        <ProvenancePanel kind="agent" id={agent.id} />
      </div>
      <AgentFooter agent={agent} busy={busy} act={act} draft={draft} hidden={tab === 'activity'} />
    </Tabs.Root>
  );
}

/** A new agent's canvas while its chat describes it: the parts it will have, nothing saved
 * yet. The model's draft (create_agent, draft: true) replaces it in place once approved. */
function NewAgentCanvas() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  return (
    <div className="ws-agents-canvas" data-draft="true">
      <header className="ws-canvas-body ws-canvas-heading ws-agents-heading">
        <p className="ws-agents-meta">{agentCopy.newAgentDraft}</p>
        <h1>{uiText('Untitled agent')}</h1>
      </header>
      {/* The tabs the draft will have, shown but not yet usable: nothing to select. */}
      <div className="ws-view-tabs ws-canvas-tabs ws-agents-tabs" aria-hidden="true">
        <button type="button" tabIndex={-1} className="active">
          {uiText('Agent')}
        </button>
        <button type="button" tabIndex={-1}>
          {uiText('Test run')}
        </button>
      </div>
      <div className="ws-canvas-body ws-agents-body">
        <NewAgentParts />
      </div>
      <CanvasFooter className="ws-agents-footer">
        <div className="ws-agents-footer-status">
          <strong data-testid="agent-status">{agentCopy.draftAuto}</strong>
          <p>{uiText('Describe the agent in the chat. Its draft appears here.')}</p>
        </div>
        <span className="ws-actions">
          <button
            type="button"
            className="ws-button"
            aria-disabled="true"
            title={uiText('Describe the agent in the chat first.')}
          >
            {uiRich('{value1} Test run', { value1: <WorkspaceIcon name="play" size={14} /> })}
          </button>
          <button
            type="button"
            className="ws-button primary"
            aria-disabled="true"
            title={uiText('Describe the agent in the chat first.')}
          >
            {uiText('Create and turn on')}
          </button>
        </span>
      </CanvasFooter>
    </div>
  );
}
